import { LogoFidexaPrincipal } from "@/assets";
import { t } from "@/context/language";
import { Instagram, Linkedin, Youtube } from "lucide-react";
import { PublicRoutes } from "@/constants/routes";
import { CHANNELS, LEGAL_NAV } from "@/pages/legal/content/company";
import { Link } from "react-router";

// Hrefs alinhados por índice com t.footer.products / t.footer.others
// (os textos vêm das traduções).
const PRODUCT_HREFS: (string | undefined)[] = [
  "/cobranca-whatsapp",
  undefined,
  "/crediario",
  "/lojistas-que-vendem-no-crediario-proprio",
  undefined,
];

// "Quem Somos" leva à newsroom: é lá que ficam fundadores, história e imprensa.
// "Canais de Atendimento" é a página /contato. "Central de Ajuda" abre o
// WhatsApp de suporte (link externo, nova aba).
const OTHER_HREFS: (string | undefined)[] = [
  PublicRoutes.NEWSROOM,
  PublicRoutes.CONTATO,
  `https://wa.me/${CHANNELS.supportWhatsapp}`,
];

const SOCIALS = [
  {
    label: "LinkedIn",
    Icon: Linkedin,
    href: "https://www.linkedin.com/company/fidexa-servicos-credito",
  },
  {
    label: "Instagram",
    Icon: Instagram,
    href: "https://www.instagram.com/fidexa.oficial/",
  },
  {
    label: "YouTube",
    Icon: Youtube,
    href: "https://www.youtube.com/@Fidexa.Oficiall",
  },
];

export function Footer() {
  return (
    <footer className="rounded-t-[2rem] bg-black px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Marca + tagline + social */}
          <div className="space-y-8 lg:col-span-4">
            <LogoFidexaPrincipal />

            <p
              className="text-2xl leading-snug font-bold text-white sm:text-3xl"
              style={{ fontFamily: "var(--font-merriweather)" }}
            >
              {t.footer.tagline[0]}
              <br />
              {t.footer.tagline[1]}
              <br />
              {t.footer.tagline[2]}
            </p>

            <div className="flex items-center gap-3">
              {SOCIALS.map(({ label, Icon, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Fidexa no ${label}`}
                  className="flex size-11 cursor-pointer items-center justify-center rounded-md bg-white/10 text-white transition-colors hover:bg-white/20"
                >
                  <Icon className="size-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Produtos */}
          <nav aria-label={t.footer.productsTitle} className="lg:col-span-3">
            <p className="mb-6 text-base font-bold text-white">
              {t.footer.productsTitle}
            </p>
            <ul className="space-y-5">
              {t.footer.products.map((item, index) => {
                const href = PRODUCT_HREFS[index];
                const content = (
                  <>
                    <span className="block font-semibold text-white transition-colors group-hover:text-primary">
                      {item.title}
                    </span>
                    <span className="block text-sm text-gray-400">
                      {item.desc}
                    </span>
                  </>
                );
                return (
                  <li key={item.title}>
                    {href ? (
                      <Link to={href} className="group block">
                        {content}
                      </Link>
                    ) : (
                      <a href="#" className="group block">
                        {content}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Outros */}
          <nav aria-label={t.footer.othersTitle} className="lg:col-span-2">
            <p className="mb-6 text-base font-bold text-white">
              {t.footer.othersTitle}
            </p>
            <ul className="space-y-4">
              {t.footer.others.map((label, index) => {
                const href = OTHER_HREFS[index];
                return (
                  <li key={label}>
                    {href?.startsWith("http") ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-white transition-colors hover:text-primary"
                      >
                        {label}
                      </a>
                    ) : href ? (
                      <Link
                        to={href}
                        className="font-medium text-white transition-colors hover:text-primary"
                      >
                        {label}
                      </Link>
                    ) : (
                      <a
                        href="#"
                        className="font-medium text-white transition-colors hover:text-primary"
                      >
                        {label}
                      </a>
                    )}
                  </li>
                );
              })}
              <li>
                <Link
                  to="/blog"
                  className="font-medium text-white transition-colors hover:text-primary"
                >
                  Blog
                </Link>
              </li>
            </ul>
          </nav>

          {/* Legal: os documentos de src/pages/legal, na mesma ordem */}
          <nav aria-label={t.footer.legalTitle} className="lg:col-span-3">
            <p className="text-ds-on-solid mb-6 text-base font-bold">
              {t.footer.legalTitle}
            </p>
            <ul className="space-y-4">
              {LEGAL_NAV.map(doc => (
                <li key={doc.key}>
                  <Link
                    to={doc.path}
                    className="text-ds-on-solid hover:text-ds-brand-400 focus-visible:ring-ds-focus cursor-pointer font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
                  >
                    {doc.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Legal */}
        <div className="mt-16 border-t border-white/10 pt-8">
          <p className="text-sm leading-relaxed text-gray-400">
            {t.footer.legal}
          </p>
          <p className="mt-4 text-sm text-gray-500">
            © {new Date().getFullYear()} Fidexa. {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
}
